/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useState } from "react";
import { ModalProps } from "..";
import { Button, Input, Modal } from "../../components";
import * as yup from "yup";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { showError } from "../../utils/error";
import toast from "react-hot-toast";

import { ICity } from "../../types";
import { createCity_V2, updateCity_V2 } from "../../Services_V2/City.service";
import { CityFormFields } from "../../pages/superadmin/cities/const";

interface Props extends ModalProps {
  refetch?: () => void;
  editableCity?: ICity | null;
}

const schema = yup.object({
  city: yup.string().required("Please enter city name"),
  state: yup.string().required("Please enter state name"),
  country: yup.string().required("Please enter country name"),
});

interface FormType {
  city: string;
  state: string;
  country: string;
}

const AddEditAdminCity: React.FC<Props> = ({
  open,
  onClose,
  editableCity,
  refetch,
}) => {
  const [loading, setLoading] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
  } = useForm<FormType>({
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    if (!open) {
      reset();
    }
  }, [open]);

  useEffect(() => {
    if (editableCity) {
      setValue(CityFormFields.CITY, editableCity.city);
      setValue(CityFormFields.STATE, editableCity.state);
      setValue(CityFormFields.COUNTRY, editableCity.country);
    }
  }, [editableCity]);

  const onSubmit = useCallback(
    async (values: FormType) => {
      setLoading(true);
      if (editableCity) {
        return updateCity(values);
      }
      createCity(values);
    },
    [onClose, refetch, editableCity]
  );

  const createCity = useCallback(async (values: FormType) => {
    try {
      const res = await createCity_V2(values);
      toast.success(res?.message);
      refetch?.();
      onClose();
      reset();
    } catch (error) {
      showError(error);
    } finally {
      setLoading(false);
    }
  }, []);

  const updateCity = useCallback(
    async (values: FormType) => {
      try {
        const res = await updateCity_V2(editableCity?._id as string, values);
        toast.success(res?.message);
        refetch?.();
        onClose();
        reset();
      } catch (error) {
        showError(error);
      } finally {
        setLoading(false);
      }
    },
    [editableCity]
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      showClose={true}
      title={editableCity ? "Update city" : "Add city"}
      loading={loading}
    >
      <form style={{ marginTop: "20px" }} onSubmit={handleSubmit(onSubmit)}>
        <Input
          fullWidth
          label="City name"
          placeholder="Enter here"
          {...register(CityFormFields.CITY)}
          error={!!errors.city}
          helperText={errors.city?.message}
        />
        <Input
          fullWidth
          label="State name"
          placeholder="Enter here"
          {...register(CityFormFields.STATE)}
          error={!!errors.state}
          helperText={errors.state?.message}
          style={{ marginTop: "12px" }}
        />
        <Input
          fullWidth
          label="Country name"
          placeholder="Enter here"
          {...register(CityFormFields.COUNTRY)}
          error={!!errors.country}
          helperText={errors.country?.message}
          style={{ marginTop: "12px" }}
        />
        <Button fullWidth type="submit" sx={{ mt: 4 }} loading={loading}>
          Save
        </Button>
      </form>
    </Modal>
  );
};

export default AddEditAdminCity;
